"use client";

import CloseIcon from "@repo/ui-system/icons/action/close/outline.svg";
import { Dialog } from "radix-ui";

import type { BestProduct } from "@/lib/api/best/keywords/schema";
import { BiEvent } from "@/lib/bi/bi.enum";
import { getStoreLogo } from "@/lib/store-logos";
import { cn } from "@/lib/utils";

import { formatReviewCount } from "../../reviews/format-review-count";
import { formatPercentOff } from "../format-percent-off";
import { getLimitedTimeDealSeconds } from "../limited-time-deal/get-limited-time-deal-seconds";
import { PromotionPopupContent } from "./promotion-popup-content";
import { usePromotionOpen } from "./use-promotion-open";

type Props = {
  product: BestProduct;
  lpv: string;
  delayMs?: number;
  className?: string;
};

export function PromotionPopup({
  product,
  lpv,
  delayMs = 15_000,
  className,
}: Props) {
  const { isOpen, setIsOpen } = usePromotionOpen({ delayMs, thresholdPx: 60 });

  const firstStore = product.stores[0];
  const popupData = {
    badge: product.mainBadge,
    discountLabel: product.percentOff
      ? formatPercentOff(product.percentOff)
      : null,
    imageUrl: product.images[0]?.url ?? "",
    title: product.name,
    reviewCount: product.reviews ? formatReviewCount(product.reviews) : null,
    ctaLabel: "Check price",
    ctaUrl: firstStore.url,
    ctaStoreName: firstStore.name,
    ctaBi: {
      eventType: BiEvent.BannerOutbound as const,
      id: product.id,
      provider: firstStore.provider ?? firstStore.name,
      seller: firstStore.name,
      lpv,
      location: "promotion-popup",
    },
    storeName: firstStore.name,
    storeLogo: getStoreLogo(firstStore),
    limitedTimeDealSeconds: getLimitedTimeDealSeconds(product.limitedTimeDeal),
  };

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={setIsOpen}
    >
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            "fixed inset-0 z-modal bg-[rgba(21,20,20,0.65)]",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            className,
          )}
        />
        <Dialog.Content
          onOpenAutoFocus={(e) => e.preventDefault()}
          className={cn(
            "fixed left-1/2 top-1/2 z-modal -translate-x-1/2 -translate-y-1/2",
            "w-[calc(100%-40px)] max-w-[320px] lg:max-w-[582px] rounded-2xl shadow-xl",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            "duration-200",
            "rounded-2xl overflow-hidden",
          )}
        >
          <Dialog.Description className="sr-only">
            A limited-time promotional offer for {popupData.title} from{" "}
            {popupData.storeName}.
          </Dialog.Description>

          <div className="absolute inset-0 rounded-2xl" />

          <Dialog.Close className="absolute top-3 right-3 lg:top-6 lg:right-6 z-20 flex items-center justify-center size-[28px] rounded-full lg:hover:bg-black/10 lg:transition-colors">
            <CloseIcon
              className="size-6 text-[#1a1c23]"
              aria-hidden="true"
            />
            <span className="sr-only">Close</span>
          </Dialog.Close>

          <PromotionPopupContent content={popupData} />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
